import React, { ChangeEvent, FocusEvent, useCallback, useContext, useEffect, useState } from 'react';

import dayjs from 'dayjs';
import { observer } from 'mobx-react-lite';
import { useNavigate } from 'react-router-dom';

import { RootStoreContext } from 'index';
import TextInput from 'components/common/TextInput/TextInput';
import useForm, { FieldValues } from 'hooks/useForm';
import { isValidPhone, validateRequiredField, validateZipCode } from 'common/utils/validation.utils';
import PhoneNumberInput from 'components/common/PhoneNumberInput/PhoneNumberInput';
import AddressInputs from 'components/common/AddressInputs/AddressInputs';
import InputSelect from 'components/common/InputSelect/InputSelect';
import Button from 'components/common/Button/Button';
import { EntryFormFieldName, IsMainContactOwnerOption } from 'components/prequalification/EntryForm/entryForm.enums';
import {
  IS_MAIN_CONTACT_OWNER_SELECT_OPTIONS,
  MAIN_CONTACT_POSITION_SELECT_OPTIONS,
} from 'components/prequalification/EntryForm/entryForm.constants';
import { validateEmail } from 'components/auth/authValidation.utils';
import { ApplicationRoute } from 'routes/routes.enums';
import Consent from 'components/common/Consent/Consent';
import { isPuertoRicoZipCode } from 'common/utils/address.utils';
import { notify } from 'components/common/Toast/toast.helpers';
import { ToastMessage } from 'components/common/Toast/toast.enums';

import styles from './EntryForm.module.scss';
import { EntryFormResultStatus } from '../prequalification.enums';

const EntryForm = () => {
  const [isLoading, setLoading] = useState<boolean>(false);
  const store = useContext(RootStoreContext);
  const navigator = useNavigate();
  const url = new URL(window.location.href);
  const params = new URLSearchParams(url.search.slice(1));
  const companyHubspotId = params.getAll('hs_object_id')[0];

  const { errors, values, setValue, setValues, validateField, handleSubmit } = useForm<FieldValues>({
    defaultValues: {
      [EntryFormFieldName.BusinessName]: '',
      [EntryFormFieldName.MainBusinessPhone]: '',
      [EntryFormFieldName.MainContactFirstName]: '',
      [EntryFormFieldName.MainContactLastName]: '',
      [EntryFormFieldName.MainContactEmail]: '',
      [EntryFormFieldName.MainContactPhone]: '',
      [EntryFormFieldName.Street]: '',
      [EntryFormFieldName.City]: '',
      [EntryFormFieldName.State]: '',
      [EntryFormFieldName.ZipCode]: '',
      [EntryFormFieldName.MainContactPosition]: '',
      [EntryFormFieldName.IsMainContactOwner]: '',
      [EntryFormFieldName.FullConsent]: false,
      [EntryFormFieldName.FullConsentAt]: null,
    },
    handleValidation: (fieldName, value) => {
      switch (fieldName) {
        case EntryFormFieldName.BusinessName: {
          return validateRequiredField(value, fieldName);
        }
        case EntryFormFieldName.MainBusinessPhone: {
          return validateRequiredField(value, fieldName) || isValidPhone(value);
        }
        case EntryFormFieldName.MainContactFirstName: {
          return validateRequiredField(value, fieldName);
        }
        case EntryFormFieldName.MainContactLastName: {
          return validateRequiredField(value, fieldName);
        }
        case EntryFormFieldName.MainContactEmail: {
          return validateRequiredField(value, fieldName) || validateEmail(value);
        }
        case EntryFormFieldName.MainContactPhone: {
          return validateRequiredField(value, fieldName) || isValidPhone(value);
        }
        case EntryFormFieldName.Street: {
          return validateRequiredField(value, fieldName);
        }
        case EntryFormFieldName.City: {
          return validateRequiredField(value, fieldName);
        }
        case EntryFormFieldName.State: {
          return validateRequiredField(value, fieldName);
        }
        case EntryFormFieldName.ZipCode: {
          return validateRequiredField(value, fieldName) || validateZipCode(value, fieldName);
        }
        case EntryFormFieldName.MainContactPosition: {
          return validateRequiredField(value, fieldName);
        }
        case EntryFormFieldName.IsMainContactOwner: {
          return validateRequiredField(value, fieldName);
        }
      }

      return '';
    },
  });

  const getPrefilledEntryForm = async () => {
    try {
      const data = await store.prequalificationStore.getPrefilledEntryForm(companyHubspotId);
      const { street, city, state, zip } = data;

      setValues({
        [EntryFormFieldName.Street]: street ?? '',
        [EntryFormFieldName.City]: city ?? '',
        [EntryFormFieldName.State]: state ?? '',
        [EntryFormFieldName.ZipCode]: zip ?? '',
      });
    } catch (error: any) {}
  };

  useEffect(() => {
    if (companyHubspotId) {
      getPrefilledEntryForm();
    }
  }, [companyHubspotId]);

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>): void => {
      const { name, value } = event.target;

      setValue(name, value);
    },
    [setValue],
  );

  const handleBlur = useCallback(
    (event: FocusEvent<HTMLInputElement>): void => {
      const name = event.target.name as keyof typeof values;

      validateField(name, values[name]);
    },
    [validateField, values],
  );

  const onSubmit = handleSubmit(async () => {
    if (isPuertoRicoZipCode(values[EntryFormFieldName.ZipCode])) {
      notify(ToastMessage.RestrictedZipCode, 'error');

      return;
    }

    setLoading(true);

    try {
      const submitEntryFormResult = await store.prequalificationStore.submitEntryForm({
        contractorApplication: {
          intermediaryBusinessName: values[EntryFormFieldName.BusinessName],
          intermediaryPhone: values[EntryFormFieldName.MainBusinessPhone],
          intermediaryCorporateAddressInformation: {
            streetName: values[EntryFormFieldName.Street],
            city: values[EntryFormFieldName.City],
            stateOrProvince: values[EntryFormFieldName.State],
            zipOrPostalCode: values[EntryFormFieldName.ZipCode],
          },
          mainContact: {
            firstName: values[EntryFormFieldName.MainContactFirstName],
            lastName: values[EntryFormFieldName.MainContactLastName],
            email: values[EntryFormFieldName.MainContactEmail],
            phoneNumber: values[EntryFormFieldName.MainContactPhone],
            position: values[EntryFormFieldName.MainContactPosition],
            isOwner: values[EntryFormFieldName.IsMainContactOwner] === IsMainContactOwnerOption.Yes,
          },
          intermediaryContactFullConsentAt: values[EntryFormFieldName.FullConsentAt],
          intermediaryRewardsProgram: params.getAll('intermediary_rewards_program')[0] || null,
        },
        generalData: {
          intermediaryUtmSource: params.getAll('utm_source')[0] || null,
          intermediaryUtmMedium: params.getAll('utm_medium')[0] || null,
          intermediaryUtmCampaign: params.getAll('utm_campaign')[0] || null,
          intermediaryCampaignId: params.getAll('campaignid')[0] || null,
          intermediaryAdGroupId: params.getAll('adgroupid')[0] || null,
          intermediaryNetwork: params.getAll('network')[0] || null,
          intermediaryDevice: params.getAll('device')[0] || null,
          intermediaryRefer: params.getAll('refer')[0] || null,
          intermediarySourceType: params.getAll('sourcetype')[0] || null,
          intermediarySourceName: params.getAll('sourcename')[0] || null,
          intermediaryPrequalLinkUsed: url.href,
          companyHubspotId: params.getAll('hs_object_id')[0],
        },
      });

      if (submitEntryFormResult.status === EntryFormResultStatus.New) {
        navigator(ApplicationRoute.PrequalificationRegistration);
      } else {
        navigator(ApplicationRoute.PrequalificationEntryFormResult);
      }
    } catch (err) {
      notify(ToastMessage.DefaultError, 'error');
    } finally {
      setLoading(false);
    }
  });

  return (
    <div className={styles.pageContainer}>
      <div className={styles.headerTitle}>Company / Contact Information</div>
      <div className={styles.entryForm}>
        <div className={styles.formBlock}>
          <div className={styles.formInput}>
            <TextInput
              className={styles.input}
              containerClassName={styles.inputContainer}
              labelTitle={EntryFormFieldName.BusinessName}
              name={EntryFormFieldName.BusinessName}
              value={values[EntryFormFieldName.BusinessName]}
              errorMessage={errors[EntryFormFieldName.BusinessName]}
              onChange={handleChange}
              onBlur={handleBlur}
            />
            <PhoneNumberInput
              className={styles.input}
              containerClassName={styles.inputContainer}
              labelTitle={EntryFormFieldName.MainBusinessPhone}
              name={EntryFormFieldName.MainBusinessPhone}
              value={values[EntryFormFieldName.MainBusinessPhone]}
              errorMessage={errors[EntryFormFieldName.MainBusinessPhone]}
              onChange={(value: any) => setValue(EntryFormFieldName.MainBusinessPhone, value)}
              onBlur={handleBlur}
            />
          </div>
        </div>
        <div className={styles.formBlock}>
          <p className={styles.blockTitle}>Business Location Address</p>
          <AddressInputs
            values={values}
            errors={errors}
            onChange={(name: string, value: any) => setValue(name, value)}
            onBlur={handleBlur}
            onInputChange={(value: any) => {
              setValues({
                [EntryFormFieldName.Street]: value.streetAddress,
                [EntryFormFieldName.City]: value.city,
                [EntryFormFieldName.State]: value.state,
                [EntryFormFieldName.ZipCode]: value.zip,
              });
            }}
            addressLabels={{
              address: EntryFormFieldName.Street,
              city: EntryFormFieldName.City,
              state: EntryFormFieldName.State,
              zip: EntryFormFieldName.ZipCode,
            }}
          />
        </div>
        <div className={styles.formBlock}>
          <p className={styles.blockTitle}>Main Contact Information</p>
          <div className={styles.formInput}>
            <TextInput
              className={styles.input}
              containerClassName={styles.inputContainer}
              labelTitle={EntryFormFieldName.MainContactFirstName}
              name={EntryFormFieldName.MainContactFirstName}
              value={values[EntryFormFieldName.MainContactFirstName]}
              errorMessage={errors[EntryFormFieldName.MainContactFirstName]}
              onChange={handleChange}
              onBlur={handleBlur}
            />
            <TextInput
              className={styles.input}
              containerClassName={styles.inputContainer}
              labelTitle={EntryFormFieldName.MainContactLastName}
              name={EntryFormFieldName.MainContactLastName}
              value={values[EntryFormFieldName.MainContactLastName]}
              errorMessage={errors[EntryFormFieldName.MainContactLastName]}
              onChange={handleChange}
              onBlur={handleBlur}
            />
          </div>
          <div className={styles.formInput}>
            <TextInput
              className={styles.input}
              containerClassName={styles.inputContainer}
              labelTitle={EntryFormFieldName.MainContactEmail}
              name={EntryFormFieldName.MainContactEmail}
              value={values[EntryFormFieldName.MainContactEmail]}
              errorMessage={errors[EntryFormFieldName.MainContactEmail]}
              onChange={handleChange}
              onBlur={handleBlur}
            />
            <PhoneNumberInput
              className={styles.input}
              containerClassName={styles.inputContainer}
              labelTitle={EntryFormFieldName.MainContactPhone}
              name={EntryFormFieldName.MainContactPhone}
              value={values[EntryFormFieldName.MainContactPhone]}
              errorMessage={errors[EntryFormFieldName.MainContactPhone]}
              onChange={(value: any) => setValue(EntryFormFieldName.MainContactPhone, value)}
              onBlur={handleBlur}
            />
          </div>
          <div className={styles.formInput}>
            <InputSelect
              items={IS_MAIN_CONTACT_OWNER_SELECT_OPTIONS}
              currentValue={values[EntryFormFieldName.IsMainContactOwner]}
              onChange={(value) => setValue(EntryFormFieldName.IsMainContactOwner, value)}
              label={EntryFormFieldName.IsMainContactOwner}
              errorMessage={errors[EntryFormFieldName.IsMainContactOwner]}
            />
            <InputSelect
              items={MAIN_CONTACT_POSITION_SELECT_OPTIONS}
              currentValue={values[EntryFormFieldName.MainContactPosition]}
              onChange={(value) => setValue(EntryFormFieldName.MainContactPosition, value)}
              label={EntryFormFieldName.MainContactPosition}
              errorMessage={errors[EntryFormFieldName.MainContactPosition]}
            />
          </div>
          <Consent
            isShowTransactionalConsent={true}
            errorMessage={errors[EntryFormFieldName.FullConsent]}
            onChange={() => {
              setValues({
                [EntryFormFieldName.FullConsent]: !!values[EntryFormFieldName.FullConsent],
                [EntryFormFieldName.FullConsentAt]: !values[EntryFormFieldName.FullConsent]
                  ? dayjs().toISOString()
                  : null,
              });
            }}
          />
        </div>
        <Button className={styles.sendButton} onClick={onSubmit} isLoading={isLoading}>
          Submit
        </Button>
      </div>
    </div>
  );
};

export default observer(EntryForm);
